<template>
    <div :class="$style.wrapper">
        <div
            :id="props.id"
            ref="wrapperEl"
            :class="[$style.scrollWrapper, props.class, { [$style.native]: nativeScroll }]"
            :style="props.style"
            data-scrollWrapper
            @wheel="handleScroll"
            @scroll="handleScroll"
        >
            <div
                ref="contentEl"
                :class="[$style.content, $style[direction], contentClass, { [$style.fixedThumb]: fixedThumb }]"
                :style="contentStyle"
            >
                <slot></slot>
            </div>
        </div>
        <Thumb
            v-for="(_, thumbDirection) in thumbs"
            :ref="(instance) => setThumbsInstance(instance, thumbDirection)"
            :key="thumbDirection"
            :autoHide="autoHide"
            :theme="theme"
            :autoHideDelay="autoHideDelay"
            :fixedThumb="
                fixedThumb ? (direction !== 'none' ? (thumbDirection === direction ? false : fixedThumb) : true) : false
            "
            :direction="thumbDirection"
            :scrollInfo="thumbDirection === 'vertical' ? scrollHeightInfo : scrollWidthInfo"
            :rightOffset="rightOffset"
        />
    </div>
</template>

<script lang="ts">
export default {
    name: 'PtScrollbar',
};
</script>

<script setup lang="ts">
import { onMounted, watch, reactive, shallowRef, computed } from 'vue';
import type { CSSProperties } from 'vue';
import clamp from 'lodash-es/clamp.js';
import Thumb from './thumb.vue';
import useMeasure from '../../hooks/useMeasure';
import type { Rect } from '../../hooks/useMeasure';

interface Props {
    id?: string;
    class?: any;
    style?: CSSProperties;
    contentClass?: string;
    contentStyle?: CSSProperties;
    direction?: 'horizontal' | 'vertical' | 'none';
    thumbMinSize?: number;
    thumbMaxSize?: number;
    autoHide?: boolean;
    autoHideDelay?: number;
    autoExpand?: boolean;
    fixedThumb?: boolean;
    throttleType?: 'throttle' | 'debounce' | 'none';
    throttleWait?: number;
    nativeScroll?: boolean;
    suppressScrollX?: boolean;
    suppressScrollY?: boolean;
    theme?: 'white' | 'dark';
    rightOffset?: string;
}

export interface ScrollInfo {
    scrollLeft: number;
    maxScrollLeft: number;
    scrollTop: number;
    maxScrollTop: number;
}

const props = withDefaults(defineProps<Props>(), {
    direction: 'vertical',
    autoHide: true,
    autoHideDelay: 900,
    throttleType: 'debounce',
    throttleWait: 333,
    fixedThumb: false,
    nativeScroll: true,
    suppressScrollX: false,
    suppressScrollY: false,
    thumbMinSize: 48,
    thumbMaxSize: Infinity,
    theme: 'white',
});

const emit = defineEmits<{
    (type: 'scroll', evt: { target: HTMLElement; scrollLeft: number; scrollTop: number } | UIEvent): void;
    (type: 'wrapperResize', evt: Rect): void;
    (type: 'contentResize', evt: Rect): void;
    (type: 'scrollInfo', evt: ScrollInfo): void;
}>();

const thumbsMapping = {
    horizontal: { el: null as unknown as HTMLElement, instance: null as unknown as typeof Thumb },
    vertical: { el: null as unknown as HTMLElement, instance: null as unknown as typeof Thumb },
};
const thumbs = computed(() => {
    const thumbs: any = {};
    for (let key in thumbsMapping) {
        if ((key === 'horizontal' && !props.suppressScrollX) || (key === 'vertical' && !props.suppressScrollY)) {
            thumbs[key] = thumbsMapping[key];
        }
    }
    return thumbs;
});
const setThumbsInstance = (instance: any, direction: keyof typeof thumbs.value) => {
    if (!thumbs.value[direction].instance) thumbs.value[direction].instance = instance;
};
onMounted(() => {
    for (const thumbDirection in thumbs.value) {
        const thumbDirectionEls = Array.from(wrapperEl.value.parentElement!.children)?.filter((element) =>
            element.hasAttribute('data-thumbPlaceHolder'),
        ) as HTMLElement[];
        thumbs.value[thumbDirection as keyof typeof thumbs.value].el = thumbDirectionEls!.find(
            (element) => element.getAttribute('data-thumbPlaceHolder') === thumbDirection,
        );
    }
});

const [wrapperEl, wrapperRect] = useMeasure({
    wait: props.throttleWait,
    type: props.throttleType,
    callback: updateMaxScrollDistance,
});
const [contentEl, contentRect] = useMeasure({
    wait: props.throttleWait,
    type: props.throttleType,
    callback: updateMaxScrollDistance,
});
let calcWrapperHeight = computed(() => wrapperRect.height - scrollHeightInfo.value.thumbSize - 5);
let calcWrapperWidth = computed(() => wrapperRect.width - scrollWidthInfo.value.thumbSize - 5);
let maxScrollTop = shallowRef(0);
let maxScrollLeft = shallowRef(0);
function updateMaxScrollDistance() {
    if (!wrapperEl.value) return;
    maxScrollTop.value = (wrapperEl.value.scrollHeight - wrapperRect.height) | 0;
    if (maxScrollTop.value < 0) maxScrollTop.value = 0;
    maxScrollLeft.value = (wrapperEl.value?.scrollWidth - wrapperRect.width) | 0;
    if (maxScrollLeft.value < 0) maxScrollLeft.value = 0;
}

let scrollWidthInfo = computed(() => {
    return {
        thumbSize:
            maxScrollLeft.value > 0
                ? clamp(
                      (wrapperRect.width / wrapperEl.value.scrollWidth) * wrapperRect.width,
                      props.thumbMinSize > wrapperRect.width ? 48 : props.thumbMinSize,
                      props.thumbMaxSize,
                  )
                : 0,
        contentMainSize: contentRect.width,
        wrapperMainSize: wrapperRect.width,
        boundaryDistance: wrapperRect.left,
    };
});
let scrollHeightInfo = computed(() => {
    return {
        thumbSize:
            maxScrollTop.value > 0
                ? clamp(
                      (wrapperRect.height / wrapperEl.value.scrollHeight) * wrapperRect.height,
                      props.thumbMinSize > wrapperRect.height ? 48 : props.thumbMinSize,
                      props.thumbMaxSize,
                  )
                : 0,
        contentMainSize: contentRect.height,
        wrapperMainSize: wrapperRect.height,
        boundaryDistance: wrapperRect.top,
    };
});

const nativeScrollInfo = reactive({
    scrollLeft: 0,
    scrollTop: 0,
    maxScrollTop: 0,
    maxScrollLeft: 0,
});

const handleScrollSimulate = (evt: WheelEvent) => {
    if (evt) {
        evt.stopPropagation();
    }
    const preScrollLeft = wrapperEl.value!.scrollLeft;
    const preScrollTop = wrapperEl.value!.scrollTop;
    const newScrollLeft = clamp(preScrollLeft + (evt?.deltaX || 0), 0, maxScrollLeft.value) | 0;
    const newScrollTop = clamp(preScrollTop + (evt?.deltaY || 0), 0, maxScrollTop.value) | 0;

    if (evt) {
        // 左右滚动时，禁止切换页面
        if (evt?.deltaX && Math.abs(evt.deltaX) !== 0) evt.preventDefault();

        // 上下滚动时，非两端时，禁止外滚
        if (newScrollTop !== 0 && newScrollTop !== nativeScrollInfo.maxScrollTop) evt.preventDefault();
    }

    wrapperEl.value!.scrollLeft = newScrollLeft;
    wrapperEl.value!.scrollTop = newScrollTop;
    if (maxScrollLeft && thumbs.value.horizontal) {
        thumbs.value.horizontal.el.style.transform = `translateX(${
            (newScrollLeft / maxScrollLeft.value) * calcWrapperWidth.value
        }px)`;
        thumbs.value.horizontal.instance.autoHideAfterScroll();
    }
    if (maxScrollTop && thumbs.value.vertical) {
        thumbs.value.vertical.el.style.transform = `translateY(${
            (newScrollTop / maxScrollTop.value) * calcWrapperHeight.value
        }px)`;
        thumbs.value.vertical.instance.autoHideAfterScroll();
    }
    nativeScrollInfo.scrollLeft = newScrollLeft;
    nativeScrollInfo.scrollTop = newScrollTop;
    emit('scroll', { target: wrapperEl.value!, scrollLeft: newScrollLeft, scrollTop: newScrollTop } as any);
};

const handleScrollNative = (evt: UIEvent) => {
    nativeScrollInfo.scrollLeft = wrapperEl.value?.scrollLeft;
    nativeScrollInfo.scrollTop = wrapperEl.value?.scrollTop;
    if (maxScrollLeft && thumbs.value.horizontal) {
        thumbs.value.horizontal.el.style.transform = `translateX(${
            (nativeScrollInfo.scrollLeft / maxScrollLeft.value) * calcWrapperWidth.value
        }px)`;
        thumbs.value.horizontal.instance.autoHideAfterScroll();
    }
    if (maxScrollTop && thumbs.value.vertical) {
        thumbs.value.vertical.el.style.transform = `translateY(${
            (nativeScrollInfo.scrollTop / maxScrollTop.value) * calcWrapperHeight.value
        }px)`;
        thumbs.value.vertical.instance.autoHideAfterScroll();
    }
    emit('scroll', evt);
};

const handleScroll = (evt: UIEvent | WheelEvent) => {
    if (props.nativeScroll) handleScrollNative(evt);
    else handleScrollSimulate(evt as WheelEvent);
};

const getScrollTop = function () {
    if (wrapperEl.value) {
        return wrapperEl.value.scrollTop || 0;
    }
};

const scrollToPos = function (scrollTop) {
    if (isNaN(scrollTop)) {
        let scrollHeight = wrapperEl.value.scrollHeight;
        let clientHeight = wrapperEl.value.clientHeight;

        switch (scrollTop) {
            case 'top':
                scrollTop = 0;
                break;
            case 'middle':
                scrollTop = scrollHeight > clientHeight ? (scrollHeight - clientHeight) / 2 : 0;
                break;
            case 'bottom':
                scrollTop = scrollHeight;
                break;
        }
    }
    wrapperEl.value.scrollTop = scrollTop;
};

watch(maxScrollTop, () => (nativeScrollInfo.maxScrollTop = maxScrollTop.value));
watch(maxScrollLeft, () => (nativeScrollInfo.maxScrollLeft = maxScrollLeft.value));
watch(wrapperRect, () => emit('wrapperResize', wrapperRect));
watch(contentRect, () => emit('contentResize', contentRect));
watch(nativeScrollInfo, () => emit('scrollInfo', nativeScrollInfo));

defineExpose({
    scrollEl: wrapperEl,
    contenEl: contentEl,
    updateMaxScrollDistance,
    getScrollTop,
    scrollToPos,
    handleScroll,
});
</script>

<style lang="scss" module>
.wrapper {
    height: 100%;
    position: relative;
    &:hover {
        > div[data-thumbPlaceHolder] {
            opacity: 1;
        }
    }
}

.scrollWrapper {
    height: 100%;
    overflow: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
        display: none;
    }
    &.native {
        overflow: auto;
    }
}

.content {
    &.vertical {
        display: block;
        &.fixedThumb {
            // display: inline-block;
            width: fit-content;
        }
    }
    &.horizontal {
        display: inline-flex;
    }
}
</style>
